import React, { useState, useEffect, useRef, useCallback } from 'react';

const Scrollbar = ({ children, className,onReachBottom,  ...props }) => {
  const contentRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const observer = useRef(null);

  const [thumbHeight, setThumbHeight] = useState(20);
  const [scrollStartPosition, setScrollStartPosition] = useState(null);
  const [initialScrollTop, setInitialScrollTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const contentInnerRef = useRef(null);

  function handleResize(ref, trackSize) {
    const { clientHeight, scrollHeight } = ref;
    setThumbHeight(Math.max((clientHeight / scrollHeight) * trackSize, 20));
  }

  function handleScrollButton(direction) {
    const current = contentRef.current;
    if (current) {
      const scrollAmount = direction === 'down' ? 200 : -200;
      current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
    }
  }

  const handleTrackClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const trackCurrent = scrollTrackRef.current;
      const contentCurrent = contentRef.current;
      if (trackCurrent && contentCurrent) {
        const { clientY } = e;
        const rect = trackCurrent.getBoundingClientRect();
        const trackTop = rect.top;
        const thumbOffset = -(thumbHeight / 2);
        const clickRatio =
          (clientY - trackTop + thumbOffset) / trackCurrent.clientHeight;
        const scrollAmount = Math.floor(
          clickRatio * contentCurrent.scrollHeight
        );
        contentCurrent.scrollTo({
          top: scrollAmount,
          behavior: 'smooth',
        });
      }
    },
    [thumbHeight]
  );

  const handleThumbPosition = useCallback(() => {
    if (
      !contentRef.current ||
      !scrollTrackRef.current ||
      !scrollThumbRef.current
    ) {
      return;
    }

    const contentTop = contentRef.current.scrollTop;
    const contentHeight = contentRef.current.scrollHeight;
    const trackHeight = scrollTrackRef.current.clientHeight;

    let newTop = (contentTop / contentHeight) * trackHeight;
    newTop = Math.min(newTop, trackHeight - thumbHeight);

    scrollThumbRef.current.style.top = `${newTop}px`;
  }, [thumbHeight]);

  const handleContentScroll = useCallback(() => {
    if (!contentRef.current) return;
  
    const { scrollTop, clientHeight, scrollHeight } = contentRef.current;
  
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      onReachBottom?.();
    }
  
    handleThumbPosition();
  }, [handleThumbPosition, onReachBottom]);

  
  const handleThumbMousedown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientY);
    if (contentRef.current) {
      setInitialScrollTop(contentRef.current.scrollTop);
    }
    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  const handleThumbMousemove = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging && contentRef.current) {
        const contentScrollHeight = contentRef.current.scrollHeight;
        const contentOffsetHeight = contentRef.current.offsetHeight;

        const deltaY =
          (e.clientY - scrollStartPosition) *
          (contentOffsetHeight / thumbHeight);
        const newScrollTop = Math.min(
          initialScrollTop + deltaY,
          contentScrollHeight - contentOffsetHeight
        );

        contentRef.current.scrollTop = newScrollTop;
      }
    },
    [isDragging, scrollStartPosition, initialScrollTop, thumbHeight]
  );

  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const ref = contentRef.current;
      const trackSize = scrollTrackRef.current.clientHeight;

      observer.current = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          handleResize(ref, trackSize);
        })
      });

      observer.current.observe(ref);
      ref.addEventListener('scroll', handleContentScroll);

      return () => {
        observer.current?.unobserve(ref);
        ref.removeEventListener('scroll', handleContentScroll);
      };
    }
  }, [handleContentScroll]);

  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const trackHeight = scrollTrackRef.current.clientHeight;
      handleResize(contentRef.current, trackHeight);
      handleThumbPosition();
    }
  }, [children]);

  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);

    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);

  const {
    gridTemplate,
    scrollerPadding,
    scrollerWidth,
    scrollHeight,
    padding,
    children: _children,
    ...domProps
  } = props;

  return (
    <div className="custom-scrollbars__container" style={{gridTemplate:gridTemplate}}>
      <div className="custom-scrollbars__content" ref={contentRef} {...domProps} style={{padding:padding}}>
      <div ref={contentInnerRef}>
        {children}
        </div>
      </div>
      <div className={`custom-scrollbars__scrollbar`} style={{padding: props.scrollerPadding}}>
        <button
          onClick={() => handleScrollButton('up')}
        style={{opacity:0}}
        >
        </button>
        <div className="custom-scrollbars__track-and-thumb" style={{ width: props.scrollerWidth , height:props.scrollHeight}}>
          <div
            className="custom-scrollbars__track"
            ref={scrollTrackRef}
            onClick={handleTrackClick}
            style={{ cursor: isDragging ? 'grabbing' : 'pointer',width: props.scrollerWidth }}
          ></div>
          <div
            className="custom-scrollbars__thumb"
            ref={scrollThumbRef}
            onMouseDown={handleThumbMousedown}
            style={{
              height: `${thumbHeight}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
              width: props.scrollerWidth
            }}
          ></div>
        </div>
       
        <button
          onClick={() =>  handleScrollButton('down')}
        style={{opacity:0}}
        >
        </button>
      </div>
    </div>
  );
};

export default Scrollbar;
